import React from 'react';
import Logo from '../../components/Logo';
import PublicNav from '../../components/PublicNav';
import { useAuth } from '../../context/AuthContext';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Linkedin } from 'lucide-react';
import { Seo } from '../../components/Seo';
import guidesData from '../../content/guides.generated.json';

const BASE_URL = 'https://verifaeye.com.au';

function categoryPillColor(cat) {
  switch (cat) {
    case 'Claiming': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'Documenting': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Recovery': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function avatarBg(name) {
  const seed = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hues = ['#0ea5e9', '#2563eb', '#7c3aed', '#0f766e', '#b45309', '#be123c'];
  return hues[seed % hues.length];
}
function initials(name) {
  return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');
}
function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function AuthorPage() {
  const { isAuthenticated } = useAuth();
  const { slug } = useParams();
  const guides = guidesData.guides || [];
  const authored = guides.filter((g) => g.author && g.author.slug === slug);
  if (authored.length === 0) return <Navigate to="/guides" replace />;

  const author = authored[0].author;
  const canonical = `${BASE_URL}/authors/${author.slug}`;
  const hasLinkedIn = typeof author.linkedin === 'string' && author.linkedin.startsWith('http');

  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: author.name,
    ...(author.description ? { description: author.description } : {}),
    ...(author.bio ? { knowsAbout: ['Home contents insurance', 'Insurance claims', 'Loss assessment'] } : {}),
    ...(author.role ? { jobTitle: author.role } : {}),
    ...(author.photo ? { image: author.photo } : {}),
    url: canonical,
    ...(hasLinkedIn ? { sameAs: [author.linkedin] } : {}),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Authors', item: `${BASE_URL}/authors` },
      { '@type': 'ListItem', position: 3, name: author.name, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="author-page">
      <Seo
        title={`${author.name} — Guides | VerifAEye`}
        description={author.description || `Guides written by ${author.name} for VerifAEye.`}
        path={`/authors/${author.slug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <PublicNav isAuthenticated={isAuthenticated} current="/guides" />

      <main className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-slate-500">
            <Link to="/guides" className="hover:text-slate-700">Guides</Link> · Author
          </p>

          <header className="mt-6 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            {author.photo ? (
              <img
                src={author.photo}
                alt={author.name}
                className="w-24 h-24 rounded-full object-cover flex-shrink-0"
                data-testid="author-page-photo"
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0"
                style={{ background: avatarBg(author.name) }}
                data-testid="author-page-initials"
              >
                {initials(author.name)}
              </div>
            )}
            <div className="flex-1">
              <h1
                className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="author-page-name"
              >
                {author.name}
              </h1>
              {author.role && (
                <p className="mt-1 text-sm font-medium uppercase tracking-wider text-blue-600" data-testid="author-page-role">
                  {author.role}
                </p>
              )}
              {author.description && (
                <p className="mt-2 text-slate-600 text-lg leading-relaxed">{author.description}</p>
              )}
              {author.bio && (
                <p className="mt-3 text-slate-600 leading-relaxed" data-testid="author-page-bio">{author.bio}</p>
              )}
              {hasLinkedIn && (
                <a
                  href={author.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="author-page-linkedin"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  <Linkedin className="w-4 h-4" /> Connect on LinkedIn
                </a>
              )}
            </div>
          </header>

          <section className="mt-14">
            <h2 className="text-sm uppercase tracking-wider text-slate-400">
              Guides by {author.name} ({authored.length})
            </h2>
            <div className="mt-6 grid gap-4">
              {authored.map((g) => (
                <Link
                  key={g.slug}
                  to={`/guides/${g.slug}`}
                  data-testid={`author-guide-${g.slug}`}
                  className="block rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-sm transition"
                >
                  <div className="flex items-baseline justify-between gap-3 text-xs uppercase tracking-wider text-slate-400">
                    <span className="flex items-center gap-2">
                      {g.category && (
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium normal-case tracking-normal ${categoryPillColor(g.category)}`}>
                          {g.category}
                        </span>
                      )}
                      <span>{g.readTime}</span>
                    </span>
                    <time dateTime={g.date}>{fmtDate(g.date)}</time>
                  </div>
                  <h3 className="mt-3 text-xl font-semibold text-slate-900">{g.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{g.description}</p>
                  <p className="mt-3 text-blue-600 text-sm font-medium">Read guide →</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="text-slate-600 text-sm ml-2">— The Proof is in the Detail</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/guides" className="text-slate-500 hover:text-slate-700">Guides</Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-700">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-700">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
